import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import { authFetch } from '../utils/api';
import Skeleton from './common/Skeleton';
import EmptyState from './common/EmptyState';

function tierColor(tier) {
  switch ((tier || '').toLowerCase()) {
    case 'trusted':
      return { color: '#10b981', bg: 'rgba(16,185,129,.12)' };
    case 'watch':
      return { color: '#2563eb', bg: 'rgba(37,99,235,.12)' };
    case 'risky':
      return { color: '#f59e0b', bg: 'rgba(245,158,11,.12)' };
    default:
      return { color: '#ef4444', bg: 'rgba(239,68,68,.12)' };
  }
}

function SummaryCard({ icon, label, value, tone }) {
  return (
    <div className="ce-stat-card">
      <div className="ce-stat-icon">{icon}</div>
      <div className="ce-stat-value" style={tone ? { color: tone } : undefined}>{value}</div>
      <div className="ce-stat-label">{label}</div>
    </div>
  );
}

export default function SupplierTrust() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tier, setTier] = useState('all');
  const [sort, setSort] = useState('supplierTrustScore');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const query = new URLSearchParams({
          tier,
          sort,
          asc: String(sort === 'repoName'),
          search,
          page: '1',
          page_size: '100',
        });
        const res = await authFetch(`${config.BASE_URL}/portfolio/supplier-trust?${query.toString()}`);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const payload = await res.json();
        if (!cancelled) {
          setData(payload);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || t('supplierTrust.loadError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [search, sort, tier, t]);

  const rows = data?.rows ?? [];
  const summary = data?.summary || {};
  const riskyRows = useMemo(() => (data?.rows ?? []).filter((row) => (row.supplierTrustScore ?? 0) < 65), [data]);

  if (loading) {
    return (
      <div className="App-body">
        <Skeleton type="cards" count={4} />
        <Skeleton type="table" count={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="App-body">
        <div className="alert alert-danger">{t('supplierTrust.loadError', 'Failed to load supplier trust overview.')}: {error}</div>
      </div>
    );
  }

  return (
    <div className="App-body">
      <div style={{ marginBottom: '1.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 className="ce-page-title">🤝 {t('supplierTrust.title', 'Supplier Trust')}</h1>
          <p className="ce-page-subtitle">{t('supplierTrust.subtitle', 'Portfolio view of malware, typosquatting, maintainer health, abandoned packages, and SLSA provenance confidence.')}</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <input
            className="form-control form-control-sm"
            style={{ maxWidth: 220 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('supplierTrust.searchPlaceholder', 'Search repositories or signals…')}
          />
          <select className="form-select form-select-sm" style={{ maxWidth: 170 }} value={tier} onChange={(e) => setTier(e.target.value)}>
            <option value="all">{t('supplierTrust.filters.all', 'All trust tiers')}</option>
            <option value="trusted">{t('supplierTrust.filters.trusted', 'Trusted')}</option>
            <option value="watch">{t('supplierTrust.filters.watch', 'Watch')}</option>
            <option value="risky">{t('supplierTrust.filters.risky', 'Risky')}</option>
            <option value="critical">{t('supplierTrust.filters.critical', 'Critical')}</option>
          </select>
          <select className="form-select form-select-sm" style={{ maxWidth: 170 }} value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="supplierTrustScore">{t('supplierTrust.sort.score', 'Lowest trust first')}</option>
            <option value="confirmedMalware">{t('supplierTrust.sort.malware', 'Confirmed malware')}</option>
            <option value="typosquats">{t('supplierTrust.sort.typosquats', 'Typosquats')}</option>
            <option value="anomalies">{t('supplierTrust.sort.anomalies', 'Anomalies')}</option>
            <option value="repoName">{t('supplierTrust.sort.name', 'Repository name')}</option>
          </select>
        </div>
      </div>

      <div className="ce-stats-grid" style={{ marginBottom: '1.5rem' }}>
        <SummaryCard icon="🗂️" label={t('supplierTrust.cards.repos', 'Analyzed repositories')} value={summary.repos ?? 0} />
        <SummaryCard icon="📉" label={t('supplierTrust.cards.average', 'Average trust score')} value={`${summary.averageScore ?? 0}/100`} tone={(summary.averageScore ?? 0) < 65 ? '#f59e0b' : '#10b981'} />
        <SummaryCard icon="🚨" label={t('supplierTrust.cards.atRisk', 'Risky + critical')} value={(summary.risky ?? 0) + (summary.critical ?? 0)} tone={((summary.risky ?? 0) + (summary.critical ?? 0)) > 0 ? '#ef4444' : '#10b981'} />
        <SummaryCard icon="🔏" label={t('supplierTrust.cards.verified', 'Verified SLSA')} value={summary.withVerifiedSlsa ?? 0} tone={(summary.withVerifiedSlsa ?? 0) > 0 ? '#2563eb' : undefined} />
        <SummaryCard icon="🦠" label={t('supplierTrust.cards.malware', 'Confirmed malware')} value={summary.confirmedMalware ?? 0} tone={(summary.confirmedMalware ?? 0) > 0 ? '#ef4444' : '#10b981'} />
        <SummaryCard icon="⚠️" label={t('supplierTrust.cards.typosquats', 'Typosquats')} value={summary.typosquats ?? 0} tone={(summary.typosquats ?? 0) > 0 ? '#f59e0b' : '#10b981'} />
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon="🤝"
          title={t('supplierTrust.emptyTitle', 'No supplier trust data yet')}
          description={t('supplierTrust.emptyDescription', 'Run SBOM, anomaly, malware, and SLSA workflows first so supplier trust signals can be aggregated.')}
        />
      ) : (
        <>
          {riskyRows.length > 0 && (
            <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
              {t('supplierTrust.warning', '{{count}} repositories are in the risky or critical supplier-trust zone.', { count: riskyRows.length })}
            </div>
          )}

          <div className="card shadow-sm">
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>{t('supplierTrust.table.repository', 'Repository')}</th>
                    <th>{t('supplierTrust.table.score', 'Trust score')}</th>
                    <th>{t('supplierTrust.table.tier', 'Tier')}</th>
                    <th>{t('supplierTrust.table.malware', 'Malware / typosquats')}</th>
                    <th>{t('supplierTrust.table.maintainers', 'Maintainer risk')}</th>
                    <th>{t('supplierTrust.table.provenance', 'Provenance')}</th>
                    <th>{t('supplierTrust.table.signals', 'Signals')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const style = tierColor(row.supplierTrustTier);
                    return (
                      <tr key={row.repoName}>
                        <td>
                          <div className="fw-semibold">
                            <Link to={`/RepoDetails/${row.repoName}`}>{row.repoName}</Link>
                          </div>
                          <div className="small text-secondary">{row.lastScanned ? `Updated ${new Date(row.lastScanned).toLocaleDateString()}` : 'No timestamp'}</div>
                        </td>
                        <td>
                          <div className="fw-semibold" style={{ color: row.supplierTrustScore < 65 ? '#f59e0b' : '#10b981' }}>{row.supplierTrustScore}/100</div>
                        </td>
                        <td>
                          <span style={{ background: style.bg, color: style.color, borderRadius: 999, padding: '0.2rem 0.55rem', fontSize: '0.75rem', fontWeight: 700 }}>
                            {row.supplierTrustTier || 'unknown'}
                          </span>
                        </td>
                        <td>
                          <div className="small">{row.confirmedMalwareCount} malware</div>
                          <div className="small text-secondary">{row.typosquatCount} typosquats</div>
                        </td>
                        <td>
                          <div className="small">{row.abandonedCount} abandoned</div>
                          <div className="small text-secondary">{row.anomalyCount} anomalies · {row.highAnomalies} high</div>
                        </td>
                        <td>
                          <div className="small">{row.slsaVerified ? `Verified ${row.slsaLevel || 'SLSA'}` : (row.slsaLevel ? `${row.slsaLevel} (${row.slsaVerificationStatus || 'review'})` : 'No SLSA')}</div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                            {(row.signals || []).slice(0, 4).map((signal) => (
                              <span key={signal} style={{ background: 'var(--bs-tertiary-bg)', border: '1px solid var(--bs-border-color)', borderRadius: 999, padding: '0.15rem 0.45rem', fontSize: '0.72rem' }}>
                                {signal}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
